import React, { useState, useEffect } from 'react';
import { FiCalendar, FiTrash2 } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

const StatusBadge = ({ status }) => {
  const styles = {
    Active: 'bg-green-100 text-green-800',
    Scheduled: 'bg-blue-100 text-blue-800',
    Expired: 'bg-gray-100 text-gray-800'
  };
  
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${styles[status]}`}>
      {status}
    </span>
  );
};

const DiscountTable = ({ discounts: initialDiscounts, onDelete, onEdit, refreshDiscounts }) => {
  const [discounts, setDiscounts] = useState(initialDiscounts || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  

  useEffect(() => {
    const fetchDiscounts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/list-discounts`);
        
        if (!response.ok) {
          const data = await response.json();
          // Check if the response is our 404 "No discounts found" message
          if (response.status === 404 && data.message === "No discounts found.") {
            setDiscounts([]);
            return;
          }
          throw new Error(data.error || 'Failed to fetch discounts');
        }
        
        const data = await response.json();
        
        // Make sure we're setting discounts to an array
        if (data && data.discounts && Array.isArray(data.discounts)) {
          setDiscounts(data.discounts);
        } else if (Array.isArray(data)) {
          setDiscounts(data);
        } else {
          console.error('Unexpected response format:', data);
          setDiscounts([]);
        }
      } catch (err) {
        setError(err.message);
        toast.error(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscounts();
  }, [refreshDiscounts]);

  const determineStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) return 'Scheduled';
    if (now > end) return 'Expired';
    return 'Active';
  };

  const handleDeleteDiscount = async (discountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/delete-discount/${discountId}`, {
        method: 'DELETE',
        headers: {}
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete discount');
      }
  
      const result = await response.json();
      toast.success(result.message || 'Discount deleted successfully');
      
      setDiscounts(prevDiscounts => prevDiscounts.filter(d => d.id !== discountId));
      
      if (onDelete) {
        onDelete(discountId);
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (discounts.length === 0) {
    return (
      <div className="bg-white p-8 rounded-lg shadow text-center">
        <div className="text-gray-500 text-lg font-medium mb-2">No discounts found</div>
        <p className="text-gray-400">Create your first discount to get started</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <ToastContainer position="top-center" autoClose={5000} />
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {discounts.map((discount) => (
            <tr key={discount.id}>
              <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{discount.name}</td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {discount.code}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-500">{discount.type}</td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-500">{discount.value}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex items-center">
                  <FiCalendar className="mr-1 text-gray-400" />
                  {discount.startDate} to {discount.endDate}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <StatusBadge status={discount.status} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button 
                  onClick={() => onEdit(discount)}
                  className="text-indigo-600 hover:text-indigo-900 mr-3"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteDiscount(discount.id)}
                  className="text-red-600 hover:text-red-900"
                >
                  <FiTrash2 />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DiscountTable;